import React from 'react';
import { Button } from './ui/button';
import { MessageCircle, Phone, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const Hero = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(key, language);

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/96891780974`, '_blank');
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Dual Background Images with Overlay */}
      <div className="absolute inset-0 grid grid-cols-1 lg:grid-cols-2">
        {/* International Destination */}
        <div className="relative h-full">
          <img
            src="https://images.unsplash.com/photo-1584467541268-b040f83be3fd?q=85"
            alt="International Travel"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E7B8C]/90 via-[#1E7B8C]/70 to-transparent"></div>
        </div>
        
        {/* Oman Landscape */}
        <div className="relative h-full hidden lg:block">
          <img
            src="https://images.unsplash.com/photo-1682197291565-2231d4b4f616?q=85"
            alt="Oman Landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-[#F4B41A]/40 via-transparent to-[#1E7B8C]/60"></div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center text-white">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 rtl:space-x-reverse bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 py-2 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <span className="w-2 h-2 bg-[#F4B41A] rounded-full animate-pulse"></span>
            <span className="text-sm font-medium">{t('servingNizwa')}</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-tight animate-in fade-in slide-in-from-bottom-6 duration-700 delay-150">
            {t('heroTitle1')}
            <span className="block text-[#F4B41A] mt-2">{t('heroTitle2')}</span>
          </h1>

          {/* Sub-headline */}
          <p className="text-lg sm:text-xl lg:text-2xl mb-10 text-white/95 max-w-3xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-8 duration-700 delay-300">
            {t('heroSubtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 animate-in fade-in slide-in-from-bottom-10 duration-700 delay-500">
            <Button
              onClick={handleWhatsAppClick}
              size="lg"
              className="group bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold px-8 py-6 rounded-xl shadow-2xl hover:shadow-[#25D366]/50 transition-all duration-300 hover:scale-105 text-lg w-full sm:w-auto"
            >
              <MessageCircle className={`${language === 'ar' ? 'ml-3' : 'mr-3'} h-6 w-6 group-hover:rotate-12 transition-transform duration-300`} />
              {t('chatWhatsApp')}
            </Button>
            
            <Button
              onClick={() => document.getElementById('callback-form').scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              variant="outline"
              className="group bg-white/95 hover:bg-white text-[#1E7B8C] border-2 border-white font-semibold px-8 py-6 rounded-xl shadow-2xl hover:shadow-white/30 transition-all duration-300 hover:scale-105 text-lg w-full sm:w-auto"
            >
              <Phone className={`${language === 'ar' ? 'ml-3' : 'mr-3'} h-6 w-6 group-hover:rotate-12 transition-transform duration-300`} />
              {t('requestCallback')}
              <ArrowRight className={`${language === 'ar' ? 'mr-2' : 'ml-2'} h-5 w-5 group-hover:translate-x-1 transition-transform duration-300`} />
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm animate-in fade-in slide-in-from-bottom-12 duration-700 delay-700">
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                <span className="text-2xl">✓</span>
              </div>
              <span className="font-medium">{t('licensedAgency')}</span>
            </div>
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                <span className="text-2xl">✓</span>
              </div>
              <span className="font-medium">{t('nizwaPickup')}</span>
            </div>
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                <span className="text-2xl">✓</span>
              </div>
              <span className="font-medium">{t('customerSupport')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white/70 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
